import KeyboardArrowDownIcon from '@material-design-icons/svg/filled/keyboard_arrow_down.svg?react';
import SearchIcon from '@material-design-icons/svg/filled/search.svg?react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';

interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  debounceDelay?: number;
  children?: ReactNode;
  className?: string;
}

export function FilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  debounceDelay = 400,
  children,
  className = '',
}: FilterBarProps) {
  const [localValue, setLocalValue] = useState(searchValue);
  const debouncedValue = useDebounce(localValue, debounceDelay);
  const prevSearchValueRef = useRef(searchValue);

  useEffect(() => {
    setLocalValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (debouncedValue !== prevSearchValueRef.current) {
      onSearchChange(debouncedValue);
      prevSearchValueRef.current = debouncedValue;
    }
  }, [debouncedValue, onSearchChange]);

  return (
    <div
      className={`bg-neutral-900 rounded-lg border border-neutral-700 p-4 ${className}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[280px]">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 fill-current text-neutral-400 size-4" />
            <input
              type="text"
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-neutral-800 border border-neutral-600 rounded-md text-sm text-white placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
            />
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

interface SelectFilterProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  className?: string;
}

export function SelectFilter({
  value,
  onChange,
  options,
  className = '',
}: SelectFilterProps) {
  return (
    <div className="relative min-w-[140px]">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full pl-3 pr-8 py-2 bg-neutral-800 border border-neutral-600 rounded-md text-sm text-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer transition-all ${className}`}
        style={{
          colorScheme: 'dark',
        }}
      >
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            className="bg-neutral-900 text-white py-2"
          >
            {option.label}
          </option>
        ))}
      </select>
      <KeyboardArrowDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 fill-current text-neutral-400 size-4 pointer-events-none" />
    </div>
  );
}
